import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const WarrantyTimelineChart = () => {
  const timelineData = [
    { month: 'Aug 2025', expiring: 12, critical: 5, warning: 4, normal: 3 },
    { month: 'Sep 2025', expiring: 8, critical: 2, warning: 3, normal: 3 },
    { month: 'Oct 2025', expiring: 15, critical: 8, warning: 4, normal: 3 },
    { month: 'Nov 2025', expiring: 6, critical: 1, warning: 2, normal: 3 },
    { month: 'Dec 2025', expiring: 22, critical: 12, warning: 6, normal: 4 },
    { month: 'Jan 2026', expiring: 9, critical: 3, warning: 3, normal: 3 },
    { month: 'Feb 2026', expiring: 18, critical: 9, warning: 5, normal: 4 },
    { month: 'Mar 2026', expiring: 11, critical: 4, warning: 4, normal: 3 },
    { month: 'Apr 2026', expiring: 7, critical: 2, warning: 2, normal: 3 },
    { month: 'May 2026', expiring: 14, critical: 6, warning: 5, normal: 3 },
    { month: 'Jun 2026', expiring: 19, critical: 10, warning: 6, normal: 3 },
    { month: 'Jul 2026', expiring: 13, critical: 5, warning: 4, normal: 4 }
  ];

  const getBarColor = (value, critical, warning) => {
    if (critical >= value * 0.6) return '#DC2626'; // red-600
    if (warning >= value * 0.4) return '#D97706'; // amber-600
    return '#059669'; // emerald-600
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg shadow-elevated p-3">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between space-x-4">
              <span className="text-error">Critical (&lt;30 days):</span>
              <span className="font-medium">{data?.critical}</span>
            </div>
            <div className="flex items-center justify-between space-x-4">
              <span className="text-warning">Warning (30-60 days):</span>
              <span className="font-medium">{data?.warning}</span>
            </div>
            <div className="flex items-center justify-between space-x-4">
              <span className="text-success">Normal (&gt;60 days):</span>
              <span className="font-medium">{data?.normal}</span>
            </div>
            <div className="border-t border-border pt-1 mt-2">
              <div className="flex items-center justify-between space-x-4 font-medium">
                <span>Total Expiring:</span>
                <span>{data?.expiring}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Warranty Timeline</h3>
            <p className="text-sm text-muted-foreground">12-month warranty expiration forecast</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-3 h-3 bg-error rounded-full"></div>
            <span className="text-muted-foreground">Critical</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <span className="text-muted-foreground">Warning</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-muted-foreground">Normal</span>
          </div>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="month" 
              stroke="#64748B"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#64748B"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="expiring" radius={[4, 4, 0, 0]}>
              {timelineData?.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getBarColor(entry?.expiring, entry?.critical, entry?.warning)} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WarrantyTimelineChart;